import { useTranslate } from '@tolgee/react'
import { TomTable, TomTableDataExportProps, useDefaultMRTOptions, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_Row, MRT_TableInstance } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportDualSheetToXlsx,
  exportMultipleCsvFiles,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { FC } from 'react'
import { ContainerJourneyService } from '../../stores/ContainerJourneyReportDataStore'
import { ContainerJourneyReportColumnDto } from '../../stores/ContainerJourneyReportViewStore'
import { ContainerJourneyReporTableDetailPanel } from '../organism/ContainerJourneyReporTableDetailPanel'
import { useContainerReportColumns } from '../tableConfigs/useContainerReportColumns'

type ContainerReportTableProps = {
  data: ContainerJourneyReportColumnDto[]
}

export const ContainerReportTable: FC<ContainerReportTableProps> = observer(({ data }) => {
  const { t } = useTranslate()
  const columns = useContainerReportColumns()

  const containerExportConfig: IDataExportConfig<ContainerJourneyReportColumnDto> = {
    containerNumber: { header: t('containerNo', 'Container N°'), type: 'string' },
    isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
    entryDate: { header: t('entryDate', 'Entry Date'), type: 'date' },
    exitDate: { header: t('exitDate', 'Exit Date'), type: 'date' },
    entryMode: { header: t('entryMode', 'Entry Mode'), type: 'string' },
    exitMode: { header: t('exitMode', 'Exit Mode'), type: 'string' },
    entryRef: { header: t('entryRef', 'Entry Ref.'), type: 'string' },
    exitRef: { header: t('exitRef', 'Exit Ref.'), type: 'string' },
    bookingRef: { header: t('bookingRef', 'Booking Ref.'), type: 'string' },
    customerName: { header: t('customer', 'Customer'), type: 'string' },
    loadStatus: { header: t('loadStatus', 'Load Status'), type: 'string' },
    dangerousStatus: { header: t('dangerousStatus', 'Dangerous'), type: 'string' },
    isReefer: { header: t('isReefer', 'Is Reefer'), type: 'boolean' },
    oogStatus: { header: t('oogStatus', 'OOG'), type: 'string' },
    hasServices: { header: t('hasServices', 'Has Services'), type: 'boolean' },
    services: { hide: true },
  }

  const serviceExportConfig: IDataExportConfig<ContainerJourneyService> = {
    containerNumber: { header: t('containerNo', 'Container N°'), type: 'string' },
    isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
    serviceName: { header: t('serviceName', 'Service name'), type: 'string' },
    startDate: { header: t('startDate', 'Start date'), type: 'date' },
    endDate: { header: t('endDate', 'End date'), type: 'date' },
  }

  const dataExport: TomTableDataExportProps<ContainerJourneyReportColumnDto> = {
    title: t('containerJourneysReport', 'Container Journeys Report'),
    enableDataExport: true,
    dataExportConfig: containerExportConfig,

    // CSV
    customExportCsv: table => {
      const { rowsToExport, servicesToExport } = prepareDataForExport(table)

      exportMultipleCsvFiles(
        t('containerJourneysReport', 'Container_Journeys'),
        rowsToExport,
        containerExportConfig,
        servicesToExport,
        serviceExportConfig,
        '_Containers_Main',
        '_Services_Details',
      )
    },

    // XLSX
    customExportXlsx: table => {
      const { rowsToExport, servicesToExport } = prepareDataForExport(table)

      exportDualSheetToXlsx(
        t('containerJourneysReport', 'Container_Journeys_Report'),
        rowsToExport,
        containerExportConfig,
        servicesToExport,
        serviceExportConfig,
        t('containersTab', 'Containers'), // Tab 1
        t('servicesTab', 'Services Details'), // Tab 2
      )
    },
  }

  const prepareDataForExport = (table: MRT_TableInstance<ContainerJourneyReportColumnDto>) => {
    const selectedRows = table.getSelectedRowModel().rows
    const rowsToExport =
      selectedRows.length > 0
        ? selectedRows.map((r: MRT_Row<ContainerJourneyReportColumnDto>) => r.original)
        : table.getFilteredRowModel().rows.map(r => r.original)

    const servicesToExport: ContainerJourneyService[] = rowsToExport.flatMap(
      (row: ContainerJourneyReportColumnDto) =>
        (row.services || []).map((service: ContainerJourneyService) => ({
          ...service,
          containerNumber: row.containerNumber ?? '',
        })),
    )

    return { rowsToExport, servicesToExport }
  }

  const defaultOptions = useDefaultMRTOptions<ContainerJourneyReportColumnDto>(
    undefined,
    dataExport,
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/container-journeys',
  )

  const table = useTomTable<ContainerJourneyReportColumnDto>(
    {
      data,
      columns: columns,
      getRowId: row => row.containerNumber!,
      enableTopToolbar: true,
      enableExpanding: true,
      enableRowActions: false,
      getRowCanExpand: () => true,
      renderDetailPanel: ({ row }) => <ContainerJourneyReporTableDetailPanel row={row} />,
      initialState: {
        ...defaultOptions.initialState,
        sorting: [{ id: 'entryDate', desc: true }],
        pagination: {
          pageIndex: 0,
          pageSize: 25,
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 260px)',
          minHeight: 'calc(100vh - 260px)',
        },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} tableName='ContainerJourneysTable' />
})
